import React, { FC, ReactElement, useLayoutEffect, useRef, useState } from "react";
import { IconButton, ListSubheader, Menu, MenuItem, SxProps, Theme, Tooltip } from "@mui/material";
import classes from "./Dropdown.module.scss";
import { dropdownStyle } from "./DropdownStyle";
import { TextEnum } from "../../SolarGikLib/TextStyles";

interface ISolarGikDropdownProps {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  openIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title?: string | ReactElement;
  buttonTooltip?: string;
  menuItems: MenuItem[];
  menuHeader?: React.ReactNode;
  menuItemCustomUi?: SxProps<Theme>;
  menuCustomUi?: SxProps<Theme>;
  buttonCustomUi?: SxProps<Theme>;
  horizontal?: "left" | "center" | "right";
  vertical?: "top" | "bottom";
  SelectedIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  selectedDefaultItemLabel?: string | ReactElement;
  alignMenuWidthToButton?: boolean;
  menuItemTooltipPlacement?: "left" | "right" | "top" | "bottom";
  disabled?: boolean;
  openOnHover?: boolean;
  onMenuItemSelected?: () => void;
}

export type MenuItem = {
  label: string | ReactElement;
  onClick: () => void;
  onAfterClose?: () => void;
  disabled?: boolean;
  tooltip?: string;
};

const SolarGikDropdown: FC<ISolarGikDropdownProps> = ({
  icon: Icon,
  openIcon: OpenIcon,
  menuItems,
  menuHeader,
  title,
  buttonTooltip,
  menuItemCustomUi,
  menuCustomUi = [],
  buttonCustomUi,
  horizontal = "left",
  vertical = "bottom",
  SelectedIcon,
  selectedDefaultItemLabel,
  alignMenuWidthToButton = false,
  menuItemTooltipPlacement = "left",
  disabled = false,
  openOnHover = false,
  onMenuItemSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [anchorWidth, setAnchorWidth] = useState<number>();
  const afterClose = useRef<(() => void) | undefined>(undefined);
  const [selectedItemLabel, setSelectedItemLabel] = useState<string | ReactElement>(
    selectedDefaultItemLabel || menuItems[0]?.label
  );
  const buttonAriaLabel = buttonTooltip ?? (typeof title === "string" ? title : undefined);

  useLayoutEffect(() => {
    if (!isOpen || !alignMenuWidthToButton || !anchorEl) {
      return;
    }
    const measure = () => setAnchorWidth(anchorEl.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(anchorEl);
    return () => observer.disconnect();
  }, [isOpen, alignMenuWidthToButton, anchorEl]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (openOnHover) {
      return;
    }

    setIsOpen(!isOpen);
    setAnchorEl(event.currentTarget);
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!openOnHover) {
      return;
    }

    setIsOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (item: MenuItem, event: React.MouseEvent<HTMLElement>) => {
    setSelectedItemLabel(item?.label);
    if (item.onAfterClose) {
      afterClose.current = item.onAfterClose;
      // Release menu focus before MUI hides it; open the next modal only after exit.
      if (event.currentTarget.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
    }
    item.onClick();
    onMenuItemSelected?.();
    setIsOpen(false);
  };

  const button = (
    <IconButton
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      className={`${classes.dropdown} ${TextEnum.h4}`}
      sx={buttonCustomUi}
      disabled={disabled}
      aria-label={buttonAriaLabel}
    >
      {title}
      {Icon && (isOpen && OpenIcon ? <OpenIcon /> : <Icon />)}
    </IconButton>
  );

  return (
    <>
      {buttonTooltip ? (
        <Tooltip title={buttonTooltip}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Menu
        sx={[
          dropdownStyle,
          ...(Array.isArray(menuCustomUi) ? menuCustomUi : [menuCustomUi]),
          alignMenuWidthToButton && { "& .MuiPaper-root": { minWidth: anchorWidth } },
        ]}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical,
          horizontal,
        }}
        transformOrigin={{
          vertical: vertical === "top" ? "bottom" : "top",
          horizontal,
        }}
        MenuListProps={{
          subheader: menuHeader ? (
            <ListSubheader role="presentation" disableSticky sx={{ padding: 0, lineHeight: "normal" }}>
              {menuHeader}
            </ListSubheader>
          ) : undefined,
          onMouseLeave: openOnHover ? () => setIsOpen(false) : undefined,
        }}
        disableRestoreFocus
        TransitionProps={{
          onExited: () => {
            const callback = afterClose.current;
            afterClose.current = undefined;
            if (callback) {
              // The dialog captures this trigger so dismissal can restore focus to it.
              anchorEl?.focus();
              callback();
            }
          },
        }}
      >
        {menuItems.map((item, index) => {
          const itemKey =
            typeof item.label === "string" ? `${item.label}-${index}` : `menu-item-${index}`;
          const menuItem = (
            <MenuItem
              disabled={item.disabled}
              key={itemKey}
              className={`${classes["menu-item"]} ${TextEnum.h5}`}
              sx={{
                ...(menuItemCustomUi || {}),
                ...(item.disabled && item.tooltip ? { pointerEvents: "auto" } : {}),
              }}
              onClick={(event: React.MouseEvent<HTMLElement>) => handleMenuItemClick(item, event)}
            >
              {item.tooltip ? (
                <Tooltip title={item.tooltip} placement={menuItemTooltipPlacement} arrow>
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span>{item.label}</span>
                    {SelectedIcon && selectedItemLabel === item?.label && <SelectedIcon />}
                  </span>
                </Tooltip>
              ) : (
                <>
                  {item?.label}
                  {SelectedIcon && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%",
                      }}
                    >
                      {selectedItemLabel === item?.label && <SelectedIcon />}
                    </div>
                  )}
                </>
              )}
            </MenuItem>
          );

          return menuItem;
        })}
      </Menu>
    </>
  );
};

export default SolarGikDropdown;
